import { useEffect } from "react";

const DEFAULT_OG = "https://customer-assets.emergentagent.com/job_mindcare-premium/artifacts/mpljmv6c_Untitled%20%281%29.png";
const SITE_NAME = "Manas Neuropsychiatry Hospital";

function setMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setLdJson(jsonObj) {
  let el = document.head.querySelector('script[type="application/ld+json"][data-page-jsonld="true"]');
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-page-jsonld", "true");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(jsonObj);
}

/**
 * useSEO – sets title, meta description, OG/Twitter tags, canonical, and optional JSON-LD.
 *
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} [opts.image]
 * @param {string} [opts.type] - "website" | "article"
 * @param {object} [opts.jsonLd]
 */
export default function useSEO({ title, description, image, type = "website", jsonLd, alternates, lang } = {}) {
  // Build-time prerender: record the head data for this route synchronously,
  // because effects never run during server rendering.
  if (typeof globalThis !== "undefined" && globalThis.__SSG_COLLECT__) {
    globalThis.__SSG_HEAD__ = {
      title: title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Deaddiction Centre & Sex Therapy Clinic, Bhavnagar`,
      description,
      image: image || DEFAULT_OG,
      type,
      jsonLd,
      alternates,
      lang,
    };
  }

  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Deaddiction Centre & Sex Therapy Clinic, Bhavnagar`;
    document.title = fullTitle;

    if (lang) document.documentElement.lang = lang;

    if (alternates?.length) {
      document.head
        .querySelectorAll('link[rel="alternate"][data-page-alt="true"]')
        .forEach((el) => el.remove());
      alternates.forEach(({ hreflang, href }) => {
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", hreflang);
        el.setAttribute("href", href);
        el.setAttribute("data-page-alt", "true");
        document.head.appendChild(el);
      });
    }

    setMeta("name", "description", description);
    setMeta("name", "robots", "index, follow");

    // OpenGraph
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:title", title || SITE_NAME);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", image || DEFAULT_OG);
    setMeta("property", "og:url", window.location.href);

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title || SITE_NAME);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image || DEFAULT_OG);

    setCanonical(window.location.href);

    if (jsonLd) setLdJson(jsonLd);
  }, [title, description, image, type, jsonLd, alternates, lang]);
}
